import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Typography, Alert, TextField } from '@mui/material';
import { LockReset } from '@mui/icons-material';
import { useChangePassword } from '../../application/useChangePassword';
import AuthCardLayout from './AuthCardLayout';
import PasswordField from './PasswordField';

/**
 * UC-4: usa useChangePassword (T27). Variante `forcePasswordChange` (pantalla completa +
 * warning + sin botón cancelar) vs. voluntaria (botón cancelar → navigate(-1)).
 */
export default function ChangePasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { error, loading, submit, forcePasswordChange } = useChangePassword();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await submit({ currentPassword, newPassword, confirmPassword });
    if (ok) navigate('/dashboard', { replace: true });
  };

  const mismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;

  return (
    <AuthCardLayout fullHeight={forcePasswordChange}>
      <Typography variant="h5" fontWeight={700} sx={{ textAlign: 'center' }}>
        <LockReset
          sx={{ fontSize: 48, color: 'primary.main', mb: 1, display: 'block', mx: 'auto' }}
        />
        {t('auth.changePassword')}
      </Typography>
      {forcePasswordChange && (
        <Alert severity="warning" sx={{ mt: 2, mb: 2, textAlign: 'left' }}>
          {t('auth.forcePasswordChangeWarning')}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <PasswordField
          fullWidth
          label={t('auth.currentPassword')}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          margin="normal"
          disabled={loading}
        />
        <PasswordField
          fullWidth
          label={t('auth.newPassword')}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          margin="normal"
          disabled={loading}
        />
        {/* UC-4: la confirmación no tiene toggle propio (spec.md), a diferencia de actual/nueva. */}
        <TextField
          fullWidth
          label={t('auth.confirmNewPassword')}
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          margin="normal"
          disabled={loading}
          error={mismatch}
          helperText={mismatch ? t('auth.passwordMismatch') : ''}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 3, py: 1.5 }}
          startIcon={<LockReset />}
        >
          {loading ? t('auth.changingPassword') : t('auth.changePassword')}
        </Button>
      </form>

      {!forcePasswordChange && (
        <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate(-1)}>
          {t('common.cancel')}
        </Button>
      )}
    </AuthCardLayout>
  );
}
